import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { isMobile } from 'react-device-detect';

const TimerWrapper = styled(Box)({
    position: 'absolute',
    top: isMobile ? '-8px' : '-20px',
    left: isMobile ? '50%' : '50%',
    width: isMobile ? '46px' : '66px',
    height: isMobile ? '46px' : '66px',
    transform: isMobile ? 'translateX(-50%)' : 'translateX(-50%)',
    zIndex: 15
});

const StyledLinearProgress = styled(LinearProgress)({
    width: '100%',
    height: '100%',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    '& .MuiLinearProgress-bar': {
        transition: 'none',
    }
});

const pulseAnimation = keyframes({
    '0%, 100%': {
        backgroundColor: 'rgba(114, 22, 45, 0.85)',
    },
    '50%': {
        backgroundColor: 'rgba(55, 10, 22, 0.85)',
    },
});

const TimerText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isUrgent',
})<{ isUrgent?: boolean }>(({ isUrgent }) => ({
    backgroundColor: 'rgba(40, 40, 54, 0.70)',
    borderRadius: '50%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    color: isUrgent ? '#FF2F48' : 'white',
    fontSize: isMobile ? '16px' : '22px',
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: isMobile ? '46px' : '66px',
    ...(isUrgent && {
        animation: `${pulseAnimation} 1s ease-in-out infinite`,
    }),
}));

interface ActionTimerProps {
    initialTime: number;
    currentTime: number;
    turnStartTime: string;
    className?: string;
    isMobileUser?: boolean;
}

const ActionTimerComponent: React.FC<ActionTimerProps> = ({ initialTime, currentTime, turnStartTime, className, isMobileUser }) => {
    // Force progress to be 100 and show initial time on first render
    const [mounted, setMounted] = useState(false);
    const [progress, setProgress] = useState(100);
    const [displayTime, setDisplayTime] = useState(initialTime);

    useEffect(() => {
        // On mount, show full time
        if (!mounted) {
            setMounted(true);
            return;
        }
        
        // After initial render, update normally
        const newProgress = Math.min(100, (currentTime / initialTime) * 100);
        setProgress(newProgress);
        setDisplayTime(currentTime);
    }, [currentTime, initialTime, mounted]);

    const getColor = (progress: number) => {
        if (progress > 50) return '#3DE361';
        return '#DA1C5A';
    };

    const isUrgent = displayTime <= 5;

    return (
        <TimerWrapper className={className}>
            <React.Fragment>
                <svg width={0} height={0}>
                    <defs>
                    <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#DA1C5A" />
                        <stop offset="100%" stopColor="#5863CC" />
                    </linearGradient>
                    </defs>
                </svg>
                <CircularProgress variant="determinate" size={isMobile ? 54 : 74} value={progress} thickness={1.5} sx={{ position: 'absolute', top: '-4px', left: '-4px', 'svg circle': { stroke: getColor(progress) }, zIndex: 15 }} />
                <TimerText isUrgent={isUrgent}>{Math.ceil(displayTime)}</TimerText>
            </React.Fragment>
        </TimerWrapper>
    );
};

// Force remount on turnStartTime change
export default React.memo(ActionTimerComponent, (prevProps, nextProps) => {
    return prevProps.turnStartTime === nextProps.turnStartTime 
        && prevProps.currentTime === nextProps.currentTime;
});
